/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { UtilityIcon } from '../SVGIcon';

import styles from '../../styles';

const Wrapper = styled.div`
  border-radius: 0.25rem;
  border: 1px solid #D8DDE6;

  @media (max-width: 767px) {
    border-radius: 0;
    border-left: 0;
    border-right: 0;
  }

  @media (min-width: 768px) {
    margin: 0 1rem;
  }
`;

const Table = styled.table`
  width: 100%;
`;

const HeaderRow = styled.tr`
  height: 2rem;
  background-color: #E6ECF2;
`;

const HeaderCell = styled.th`
  padding-left: 0.7rem;
  @media (max-width: 767px) {
    &:first-child {
      padding-left: 1.25rem;
    }
  }

  ${styles.fonts.headingLabelCaps}

  align-items: center;
  text-transform: uppercase;

  width: ${(p) => {
    return p.sizeLarge / 12 * 100;
  }}%;
  display ${(p) => {
    return (p.hideLarge ? 'none' : 'table-cell');
  }};

  @media (max-width: 1023px) {
    width: ${(p) => {
    return p.sizeMedium / 12 * 100;
  }}%;
    display ${(p) => {
    return (p.hideMedium ? 'none' : 'table-cell');
  }};
  }

  @media (max-width: 767px) {
    width: ${(p) => {
    return p.sizeSmall / 12 * 100;
  }}%;
    display ${(p) => {
    return (p.hideSmall ? 'none' : 'table-cell');
  }};
  }
`;

const BucketRow = styled.tr`
  height: 2.5rem;
  background-color: #F4F6F9;
`;

const BucketCell = styled.th`
  padding: 0 0.75rem 0 0.75rem;
  @media (max-width: 767px) {
    &:first-child {
      padding-left: 1.25rem;
    }
  }

  ${styles.fonts.headingLabelCaps}
  color: ${styles.colors.biscay};
  border-top: 1px solid #D8DDE6;
`;

const SpreadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DataRow = styled.tr`
  &:hover {
    background-color: #F0F8FC;
  }
`;

const DataCell = styled.td`
  padding-left: 0.75rem;
  @media (max-width: 767px) {
    &:first-child {
      padding-left: 1.25rem;
    }
  }

  height: 2.5rem;
  vertical-align: middle;
  font-size: 13px;
  color: #16325C;

  border-top: 1px solid #D8DDE6;

  display ${(p) => {
    return (p.hideLarge ? 'none' : 'table-cell');
  }};

  @media (max-width: 1023px) {
    display ${(p) => {
    return (p.hideMedium ? 'none' : 'table-cell');
  }};
  }

  @media (max-width: 767px) {
    display ${(p) => {
    return (p.hideSmall ? 'none' : 'table-cell');
  }};
  }
`;

const ClickableButton = styled.button`
  text-transform: uppercase;
`;

class BucketedDataTable extends React.Component {
  render() {
    const {
      bucketedList: { buckets, actions, columns }, renderEmptyRow, className, timezone
    } = this.props;

    return (
      <Wrapper className={className}>
        <Table>
          <thead>
            <HeaderRow>
              {columns.map((column, i) => {
                return (
                  <HeaderCell
                    id='date'
                    scope='col'
                    key={i}
                    sizeLarge={column.sizeLarge}
                    sizeMedium={column.sizeMedium}
                    sizeSmall={column.sizeSmall}
                    hideSmall={column.hideSmall}
                    hideMedium={column.hideMedium}
                    hideLarge={column.hideLarge}
                  >
                    {column.title}
                  </HeaderCell>
                );
              })}
            </HeaderRow>
          </thead>
          <tbody>
            {buckets.reduce((tableRows, bucket, i) => {
              tableRows.push([
                <BucketRow
                  key={bucket.title + '_' + (bucket.open ? 'up' : 'down')}
                  onClick={() => {
                    bucket.toggle();
                  }}>
                  <BucketCell colSpan='8' scope='colgroup'>
                    <SpreadWrapper>
                      <div>
                        {bucket.rows.length
                          ? <ClickableButton className="slds-button">{bucket.title}</ClickableButton> : bucket.title}
                      </div>
                      {(bucket.rows.length > 0 || renderEmptyRow) && <UtilityIcon icon={bucket.open ? 'up' : 'down'} />}
                    </SpreadWrapper>
                  </BucketCell>
                </BucketRow>
              ]);

              if (bucket.open) {
                Array.prototype.push.apply(tableRows, bucket.rows.map((row, ii) => {
                  return (
                    <DataRow key={i + '_' + ii}>
                      {columns.map((column, iii) => {
                        return (
                          <DataCell
                            key={i + '_' + ii + '_' + iii}
                            hideSmall={column.hideSmall}
                            hideMedium={column.hideMedium}
                            hideLarge={column.hideLarge}
                          >
                            {column.render ? column.render(row, { timezone }) : row[column.key]}
                          </DataCell>
                        );
                      })}
                    </DataRow>
                  );
                }));

                if (bucket.rows.length === 0 && renderEmptyRow) {
                  tableRows.push(<DataRow key={i + '_empty'}>
                    <DataCell colSpan='8'>
                      {renderEmptyRow(bucket)}
                    </DataCell>
                  </DataRow>);
                }
              }
              return tableRows;
            }, [])}
          </tbody>
        </Table>
      </Wrapper>
    );
  }
}

BucketedDataTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    sizeLarge: PropTypes.number,
    sizeMedium: PropTypes.number,
    sizeSmall: PropTypes.number,
    hideSmall: PropTypes.bool,
    hideMedium: PropTypes.bool,
    hideLarge: PropTypes.bool,
  }))
};

export default connect((state) => {
  return { timezone: state.timezonePicker };
})(BucketedDataTable);
